// =================================================================
// DialecteBench.js — Session 55 P4
// Page admin : scoring manuel des prompts optimisés par Le Dialecte.
// Chaque row = une optimisation Dialecte qui a produit une image.
// L'admin note 1-10 + commente → feedback pour V3 (ré-injection auto).
// =================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Wand2, RefreshCw, Star, MessageSquare, Loader2, ExternalLink } from 'lucide-react';
import { toast } from '../ui/Toast';
import { signUrl } from '../../services/signedUrlApi';
import useSignedSrc from '../../hooks/useSignedSrc';

const STATUS_LABELS = {
    all: 'Toutes',
    generated: 'À scorer',
    scored: 'Déjà scorées',
    optimized: 'Pas d\'image',
};

const SOURCE_LABELS = {
    studio: 'Studio',
    chat_project: 'Chat projet',
    chat_general: 'Chat général',
};

const fmtCost = (c) => {
    if (!c || c <= 0) return '—';
    if (c < 0.01) return `$${c.toFixed(5)}`;
    return `$${c.toFixed(4)}`;
};

const DialecteBench = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('generated');
    const [offset, setOffset] = useState(0);
    const [lightbox, setLightbox] = useState(null);

    const limit = 20;

    const load = useCallback(async (keepOffset = false) => {
        setLoading(true);
        try {
            const currentOffset = keepOffset ? offset : 0;
            const token = localStorage.getItem('oracle_token');
            const res = await fetch(
                `/api/admin/dialecte-bench?status=${status}&limit=${limit}&offset=${currentOffset}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setItems(data.items || []);
            setTotal(data.total || 0);
            if (!keepOffset) setOffset(0);
        } catch (err) {
            toast.error(`Erreur chargement: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [status, offset]);

    useEffect(() => { load(false); /* eslint-disable-next-line */ }, [status]);

    return (
        <div
            id="dialecte-bench"
            style={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '24px 24px 32px 24px',
            }}
        >
            <style>{`
                #dialecte-bench::-webkit-scrollbar { width: 8px; }
                #dialecte-bench::-webkit-scrollbar-track { background: transparent; }
                #dialecte-bench::-webkit-scrollbar-thumb {
                    background: rgba(167,139,250,0.25);
                    border-radius: 4px;
                }
                #dialecte-bench::-webkit-scrollbar-thumb:hover {
                    background: rgba(167,139,250,0.45);
                }
                #dialecte-bench-tabs::-webkit-scrollbar { height: 6px; }
                #dialecte-bench-tabs::-webkit-scrollbar-track { background: transparent; }
                #dialecte-bench-tabs::-webkit-scrollbar-thumb {
                    background: rgba(167,139,250,0.20);
                    border-radius: 3px;
                }
                #dialecte-bench-tabs::-webkit-scrollbar-thumb:hover {
                    background: rgba(167,139,250,0.40);
                }
            `}</style>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Wand2 size={20} style={{ color: '#a78bfa' }} />
                        BENCH prompts Dialecte
                    </h2>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        Scoring manuel des optimisations Dialecte — feedback pour la boucle d'apprentissage.
                    </div>
                </div>
                <button
                    onClick={() => load(false)}
                    disabled={loading}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '8px 14px', borderRadius: '8px',
                        background: 'transparent', border: '1px solid var(--card-border)',
                        color: 'var(--text)', fontSize: '13px', cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
                    Rafraîchir
                </button>
            </div>

            {/* Filtres — onglets scrollables horizontalement si largeur restreinte */}
            <div
                id="dialecte-bench-tabs"
                style={{
                    display: 'flex', gap: '8px', marginBottom: '20px',
                    overflowX: 'auto', overflowY: 'hidden',
                    paddingBottom: '4px',
                    scrollbarWidth: 'thin',
                }}
            >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setStatus(key)}
                        style={{
                            padding: '6px 14px', borderRadius: '20px',
                            background: status === key ? 'rgba(139,92,246,0.15)' : 'transparent',
                            border: `1px solid ${status === key ? '#8b5cf6' : 'var(--card-border)'}`,
                            color: status === key ? '#a78bfa' : 'var(--text-secondary)',
                            fontSize: '12px', fontWeight: status === key ? 600 : 400, cursor: 'pointer',
                            flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                    >
                        {label}
                    </button>
                ))}
                <div style={{ flex: 1, minWidth: 8 }} />
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', alignSelf: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {total} résultat{total > 1 ? 's' : ''}
                </div>
            </div>

            {/* Liste */}
            {items.length === 0 && !loading ? (
                <div style={{
                    padding: '40px', textAlign: 'center',
                    color: 'var(--text-secondary)', opacity: 0.6,
                    border: '1px dashed var(--card-border)', borderRadius: '12px',
                }}>
                    Aucun prompt à afficher pour ce filtre.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {items.map(item => (
                        <BenchItemCard
                            key={item.id}
                            item={item}
                            onScored={() => load(true)}
                            onLightbox={(url) => setLightbox(url)}
                        />
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {lightbox && (
                <div onClick={() => setLightbox(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'zoom-out', backdropFilter: 'blur(4px)',
                    }}>
                    <img src={lightbox} alt="preview" style={{ maxWidth: '92%', maxHeight: '92%', borderRadius: '8px' }} />
                </div>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

// ─── Card par item ──────────────────────────────────────────────
const BenchItemCard = ({ item, onScored, onLightbox }) => {
    const [score, setScore] = useState(item.score_global || 7);
    const [notes, setNotes] = useState(item.score_notes || '');
    const [saving, setSaving] = useState(false);

    const handleScore = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('oracle_token');
            const res = await fetch(`/api/admin/dialecte-bench/${item.id}/score`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ score, notes: notes || null }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            toast.success(`Noté ${score}/10`);
            onScored();
        } catch (err) {
            toast.error(`Erreur: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const hasAsset = !!item.asset_url;
    const signedThumb = useSignedSrc(hasAsset ? item.asset_url : null);
    const isScored = item.status === 'scored';

    const handleOpenLightbox = async () => {
        if (!hasAsset) return;
        try {
            const signed = await signUrl(item.asset_url);
            onLightbox(signed);
        } catch {
            toast.error('Lien expiré, réessaye.');
        }
    };

    return (
        <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: '12px', padding: '18px',
            display: 'grid', gridTemplateColumns: hasAsset ? '200px 1fr 280px' : '1fr 280px', gap: '18px',
        }}>
            {/* Thumbnail image */}
            {hasAsset && (
                <div
                    onClick={handleOpenLightbox}
                    style={{
                        width: '200px', height: '200px', cursor: 'zoom-in',
                        background: 'var(--input-bg)', borderRadius: '8px',
                        overflow: 'hidden', border: '1px solid var(--card-border)',
                    }}
                >
                    <img src={signedThumb || ''} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
            )}

            {/* Content central : prompts + rationale */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
                {/* Badges */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', fontSize: '11px' }}>
                    <span style={{
                        padding: '2px 8px', borderRadius: '10px',
                        background: 'rgba(139,92,246,0.1)', color: '#a78bfa', fontWeight: 600,
                    }}>
                        {item.target_model_label || item.target_model_id}
                    </span>
                    <span style={{
                        padding: '2px 8px', borderRadius: '10px',
                        background: 'var(--input-bg)', color: 'var(--text-secondary)',
                    }}>
                        {SOURCE_LABELS[item.source] || item.source}
                    </span>
                    {item.deep && (
                        <span style={{
                            padding: '2px 8px', borderRadius: '10px',
                            background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontWeight: 600,
                        }}>
                            Approfondi
                        </span>
                    )}
                    <span style={{ color: 'var(--text-secondary)', alignSelf: 'center' }}>
                        {fmtCost(item.dialecte_cost_usd)} · {item.chunks_used} chunks · {item.base_mode}
                    </span>
                </div>

                <div>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                        Brouillon
                    </div>
                    <div style={{
                        fontSize: '13px', color: 'var(--text)',
                        background: 'var(--input-bg)', padding: '8px 10px', borderRadius: '6px',
                        whiteSpace: 'pre-wrap', maxHeight: '80px', overflow: 'auto',
                    }}>
                        {item.raw_prompt}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                        Optimisé
                    </div>
                    <div style={{
                        fontSize: '12.5px', color: 'var(--text)',
                        background: 'rgba(139,92,246,0.05)', padding: '8px 10px', borderRadius: '6px',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto',
                        border: '1px solid rgba(139,92,246,0.15)',
                    }}>
                        {item.optimized_prompt}
                    </div>
                </div>
                {item.rationale && (
                    <div style={{
                        fontSize: '12px', color: 'var(--text-secondary)',
                        borderLeft: '2px solid rgba(139,92,246,0.4)', paddingLeft: '8px',
                        fontStyle: 'italic', lineHeight: 1.5,
                    }}>
                        {item.rationale}
                    </div>
                )}
            </div>

            {/* Scoring panel à droite */}
            <div style={{
                display: 'flex', flexDirection: 'column', gap: '10px',
                padding: '12px', background: 'var(--input-bg)', borderRadius: '8px',
            }}>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>
                    {isScored ? `Score actuel` : `Noter ce prompt`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="range" min="1" max="10" step="1"
                        value={score}
                        onChange={e => setScore(parseInt(e.target.value))}
                        style={{ flex: 1 }}
                    />
                    <span style={{
                        fontSize: '18px', fontWeight: 700,
                        color: score >= 8 ? '#10b981' : score >= 5 ? '#f59e0b' : '#ef4444',
                        minWidth: '32px', textAlign: 'right',
                    }}>
                        {score}
                    </span>
                </div>
                <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Notes (ex: trop générique, manque de détail technique…)"
                    rows={3}
                    style={{
                        fontSize: '12px', padding: '6px 8px',
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                        borderRadius: '6px', color: 'var(--text)', resize: 'vertical',
                        fontFamily: 'inherit',
                    }}
                />
                <button
                    onClick={handleScore}
                    disabled={saving}
                    style={{
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                        color: '#fff', fontSize: '12px', fontWeight: 600,
                        cursor: saving ? 'wait' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                >
                    {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Star size={13} />}
                    {isScored ? 'Re-noter' : 'Enregistrer'}
                </button>
                {item.scored_at && (
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                        Dernière note par {item.scored_by} · {new Date(item.scored_at).toLocaleDateString('fr-FR')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DialecteBench;
